import React from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import type { TemplateItem } from 'inspection-types';
import { S } from 'components/typography';

import { formatQuestionOrdinal } from '../../helpers/inspectionTemplateCustomizeHelpers';

import styles from './InspectionTemplateCustomizeSectionCard.scss';

type Props = {
  item: TemplateItem;
  sectionIndex: number;
  /** 0-based second segment for `{section}.{ordinal}` labels (computed by parent). */
  itemOrdinalSegmentIndex: number;
  /** Tooltip when remove is disabled (e.g. feature off). */
  removeItemDisabledTitle: string;
  /** Row is shown as removed (strikethrough) after a successful remove. */
  isRemovedPresentation: boolean;
  onRemoveItem?: () => void;
  isRemoveLoading?: boolean;
  onRestoreItem?: () => void;
  isRestoreLoading?: boolean;
};

export function Item({
  item,
  sectionIndex,
  itemOrdinalSegmentIndex,
  removeItemDisabledTitle,
  isRemovedPresentation,
  onRemoveItem,
  isRemoveLoading = false,
  onRestoreItem,
  isRestoreLoading = false,
}: Props) {
  const { t } = useTranslation();

  const removalDisabled = !onRemoveItem || isRemovedPresentation;
  const removeTitle =
    removalDisabled && !onRemoveItem ? removeItemDisabledTitle : undefined;

  let rowActionContent: React.ReactNode;

  if (isRemovedPresentation) {
    if (onRestoreItem && isRestoreLoading) {
      rowActionContent = (
        <div
          className={styles.rowActionLoadingWrap}
          role="status"
          aria-label={t(
            'web.admin.inspections.templateCustomize.restoreItemLoadingAria'
          )}
          data-testid="restore-item-loading"
        >
          <span className={styles.rowActionSpinner} aria-hidden />
        </div>
      );
    } else {
      rowActionContent = (
        <button
          type="button"
          className={classNames(
            styles.rowActionButton,
            onRestoreItem
              ? styles.rowActionButtonInteractive
              : styles.rowActionButtonRestorePlaceholder
          )}
          disabled={!onRestoreItem}
          aria-label={
            onRestoreItem
              ? t('web.admin.inspections.templateCustomize.restoreItemAria')
              : t(
                  'web.admin.inspections.templateCustomize.revertItemPlaceholderAria'
                )
          }
          title={
            onRestoreItem
              ? t('web.admin.inspections.templateCustomize.restoreItemTitle')
              : t(
                  'web.admin.inspections.templateCustomize.revertItemPlaceholderTitle'
                )
          }
          onClick={onRestoreItem ? () => onRestoreItem() : undefined}
        >
          <Icon name="undo" set={ICON_SET_FONTAWESOME} type="far" />
        </button>
      );
    }
  } else if (isRemoveLoading) {
    rowActionContent = (
      <div
        className={styles.rowActionLoadingWrap}
        role="status"
        aria-label={t(
          'web.admin.inspections.templateCustomize.removeItemLoadingAria'
        )}
        data-testid="remove-item-loading"
      >
        <span className={styles.rowActionSpinner} aria-hidden />
      </div>
    );
  } else {
    rowActionContent = (
      <button
        type="button"
        className={classNames(
          styles.rowActionButton,
          styles.rowActionButtonInteractive
        )}
        disabled={removalDisabled}
        title={removeTitle}
        aria-label={t('web.admin.inspections.templateCustomize.removeItemAria')}
        onClick={onRemoveItem ? () => onRemoveItem() : undefined}
      >
        <Icon name="trash-alt" set={ICON_SET_FONTAWESOME} type="far" />
      </button>
    );
  }

  return (
    <div className={styles.itemRow}>
      <div className={styles.rowLead} aria-hidden />
      <S className={styles.itemText}>
        <span>{formatQuestionOrdinal(sectionIndex, itemOrdinalSegmentIndex)}{' '}</span>
        {isRemovedPresentation ? (
          <>
            <span className={styles.itemLabelRemoved}>{item.label}</span>
            <span className={styles.removedBadge}>
              {' '}
              {t('web.admin.inspections.templateCustomize.removedBadge')}
            </span>
          </>
        ) : (
          item.label
        )}
      </S>
      <div className={styles.rowActionCell}>{rowActionContent}</div>
    </div>
  );
}
